'use client';

import { useState } from 'react';
import { defineMessage, defineMessages, useIntl } from 'react-intl';

import {
  useCookies,
  type CookieOptions,
} from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { Link } from '@packages/modules/src/Link/Link';

import { bypassBlockMessages } from './messages';

const { title, description, action } = defineMessages({
  title: {
    id: 'appbanner.title',
    defaultMessage: 'Unsere App',
  },
  description: { id: 'appbanner.description', defaultMessage: 'Kostenlos im Store' },
  action: { id: 'appbanner.action', defaultMessage: 'Jetzt anzeigen' },
});

export const appBannerCookieName = 'bannerDisabled';
const closeBannerMessage = defineMessage({ id: 'appBanner.close', defaultMessage: 'Schließen' });

const COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  maxAge: 86400, // 90 days in seconds
  secure: false, // true would be nice but this is what intershop defines,
  sameSite: 'lax',
  httpOnly: false,
};

export const AppBanner = () => {
  const { getCookies, setCookie } = useCookies();
  const {
    appBanner: { appUrl },
  } = useConfig();
  const [showBanner, setShowBanner] = useState(true);

  const { formatMessage } = useIntl();

  const handleClose = () => {
    setShowBanner(false);
    setCookie(null, appBannerCookieName, 'true', COOKIE_OPTIONS);
  };

  const cookies = getCookies();

  if (!showBanner || cookies[appBannerCookieName]) {
    return null;
  }

  return (
    <div className="bg-color-grey-light relative p-2" data-testid="app-banner">
      <BypassBlock
        skipScrollIntoView
        buttonText={formatMessage(bypassBlockMessages.bypassAppBanner)}
      >
        <Button
          variant="icon-only"
          className="absolute top-0.5 right-0.5 h-4"
          onClick={handleClose}
          aria-label={formatMessage(closeBannerMessage)}
        >
          <div className="icon-emp-cancel" />
        </Button>
        <Link href={appUrl} className="flex items-center gap-2 no-underline">
          <div className="bg-color-background-base flex rounded-r-[0.5rem]">
            <div className="icon-emp-app-icon h-[4rem] w-[4rem]" />
          </div>
          <div className="text-color-text-dark">
            <p>{formatMessage(title)}</p>
            <p className="font-bold">{formatMessage(description)}</p>
            <p className="font-bold">{formatMessage(action)}</p>
          </div>
        </Link>
      </BypassBlock>
    </div>
  );
};
